import React, { useState, useEffect, useMemo } from "react";
import "./ApprovedReports.css";
import {
  getApprovedOvertimeReports,
  getApprovedDayoffReports,
} from "../../../api/reportsApi";
import Preloader from "../../Preloader/Preloader";

function ApprovedReports() {
  const [overtimeReports, setOvertimeReports] = useState([]);
  const [dayoffReports, setDayoffReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overtime"); // "overtime" или "dayoff"

  // Состояние для фильтрации по ФИО
  const [employeeFilter, setEmployeeFilter] = useState("");

  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchApprovedReports = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const overtimeData = await getApprovedOvertimeReports();
      const formattedOvertimeReports =
        overtimeData?.overtimes?.map((report) => {
          const dateObj = new Date(report.fact_date_start);
          return {
            id: report.id,
            userName: `${report.surname} ${report.name} ${report.patronymic}`,
            date: dateObj.toLocaleDateString("ru-RU"),
            dateObj: dateObj, // Сохраняем объект Date для сортировки и фильтрации
            project: report.project_name,
            hours: `${Math.floor(report.time_spent / 3600)}ч ${Math.floor(
              (report.time_spent % 3600) / 60
            )}м`,
            status: Object.values(report.status).includes("Согласован")
              ? "Согласован"
              : "Не согласован",
            group: report.group,
            role: report.role,
            validUntil: report.valid_until
              ? new Date(report.valid_until).toLocaleDateString("ru-RU")
              : null,
            daysLeft: report.days_left,
            isUsed: report.is_used,
            dateOfUse: report.date_of_use
              ? new Date(report.date_of_use).toLocaleDateString("ru-RU")
              : null,
          };
        }) || [];

      setOvertimeReports(formattedOvertimeReports);

      const dayoffData = await getApprovedDayoffReports();
      const formattedDayoffReports =
        dayoffData?.days_off?.map((report) => {
          const dateObj = new Date(report.date_of_use);
          return {
            id: report.id,
            userName: `${report.surname} ${report.name} ${report.patronymic}`,
            date: dateObj.toLocaleDateString("ru-RU"),
            dateObj: dateObj, // Сохраняем объект Date для сортировки и фильтрации
            hours: Math.floor(report.time / 3600),
            status: "Согласован", // Все отгулы в этом эндпоинте уже согласованы
            group: report.groups[0],
            role: report.roles[0],
          };
        }) || [];

      setDayoffReports(formattedDayoffReports);

      // Устанавливаем общее количество элементов в зависимости от активной вкладки
      setTotalItems(
        activeTab === "overtime"
          ? formattedOvertimeReports.length
          : formattedDayoffReports.length
      );
    } catch (err) {
      setError("Ошибка при загрузке согласованных отчетов");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedReports();
  }, []);

  // Функция для фильтрации и сортировки отчетов по ФИО
  const getFilteredAndSortedReports = useMemo(() => {
    const reports = activeTab === "overtime" ? overtimeReports : dayoffReports;

    // Фильтрация по ФИО
    let filtered = reports;

    if (employeeFilter.trim()) {
      const searchTerm = employeeFilter.trim().toLowerCase();
      filtered = filtered.filter((report) => {
        return report.userName.toLowerCase().includes(searchTerm);
      });
    }

    // Сортировка от новых к старым
    filtered = [...filtered].sort((a, b) => {
      return b.dateObj - a.dateObj; // От новых к старым
    });

    return filtered;
  }, [overtimeReports, dayoffReports, activeTab, employeeFilter]);

  // Обновляем общее количество элементов при смене вкладки или фильтрах
  useEffect(() => {
    setTotalItems(getFilteredAndSortedReports.length);
    setCurrentPage(1); // Сбрасываем на первую страницу при смене вкладки или фильтрах
  }, [activeTab, getFilteredAndSortedReports.length]);

  // Функция для получения плоского списка отчетов в зависимости от активной вкладки
  // Теперь используем отфильтрованные и отсортированные отчеты
  const getCurrentReports = () => {
    return getFilteredAndSortedReports;
  };

  // Вычисляем общее количество страниц
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Получаем текущие отображаемые отчеты с учетом пагинации
  const getPaginatedReports = () => {
    const reports = getCurrentReports();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return reports.slice(startIndex, endIndex);
  };

  // Функция для изменения количества элементов на странице
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении количества элементов
  };

  // Функция для перехода на предыдущую страницу
  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  // Функция для перехода на следующую страницу
  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
  };

  // Функция для перехода на конкретную страницу
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Функция для отображения номеров страниц
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Максимальное количество видимых номеров страниц

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`approved-reports__page-number ${
            currentPage === i ? "active" : ""
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return pageNumbers;
  };

  // Получаем отфильтрованные отчеты с учетом пагинации
  const paginatedReports = getPaginatedReports();

  // Создаем новый объект сгруппированных отчетов, содержащий только отфильтрованные данные
  const paginatedGroupedReports = useMemo(() => {
    const groups = {};

    paginatedReports.forEach((report) => {
      if (!groups[report.group]) {
        groups[report.group] = {};
      }
      if (!groups[report.group][report.role]) {
        groups[report.group][report.role] = {};
      }
      if (!groups[report.group][report.role][report.userName]) {
        groups[report.group][report.role][report.userName] = [];
      }
      groups[report.group][report.role][report.userName].push(report);
    });

    return groups;
  }, [paginatedReports]);

  if (isLoading) return <Preloader />;

  const hasReports = getFilteredAndSortedReports.length > 0;

  return (
    <div className="approved-reports">
      <h2 className="approved-reports__title">История согласованных отчетов</h2>

      {error && <div className="approved-reports__error">{error}</div>}

      {/* Фильтр по ФИО */}
      <div className="approved-reports__filters">
        <div className="approved-reports__filters-row">
          <div className="approved-reports__filter-item">
            <label>ФИО сотрудника:</label>
            <input
              type="text"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              placeholder="Введите ФИО для поиска"
              className="approved-reports__employee-input"
            />
          </div>
          {employeeFilter && (
            <div className="approved-reports__filter-item">
              <label style={{ visibility: "hidden" }}>Сброс</label>
              <button
                className="approved-reports__clear-filter"
                onClick={() => {
                  setEmployeeFilter("");
                }}
              >
                Сбросить фильтр
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="approved-reports__tabs">
        <button
          className={`approved-reports__tab ${
            activeTab === "overtime" ? "active" : ""
          }`}
          onClick={() => setActiveTab("overtime")}
        >
          Переработки
        </button>
        <button
          className={`approved-reports__tab ${
            activeTab === "dayoff" ? "active" : ""
          }`}
          onClick={() => setActiveTab("dayoff")}
        >
          Отгулы
        </button>
      </div>

      {!hasReports ? (
        <div className="approved-reports__empty">
          {activeTab === "overtime"
            ? "Согласованные переработки отсутствуют"
            : "Согласованные отгулы отсутствуют"}
        </div>
      ) : (
        <>
          <div className="approved-reports__content">
            {Object.entries(paginatedGroupedReports).map(([group, roles]) => (
              <div key={group} className="approved-reports__group">
                <h3 className="approved-reports__group-title">{group}</h3>
                {Object.entries(roles).map(([role, users]) => (
                  <div key={role} className="approved-reports__role">
                    <h4 className="approved-reports__role-title">{role}</h4>
                    {Object.entries(users).map(([userName, reports]) => (
                      <div key={userName} className="approved-reports__user">
                        <h5 className="approved-reports__user-title">
                          {userName}
                        </h5>
                        <table className="approved-reports__table">
                          <thead>
                            <tr>
                              <th>Дата</th>
                              {activeTab === "overtime" && <th>Проект</th>}
                              <th>Часы</th>
                              <th>Статус</th>
                              {activeTab === "overtime" && (
                                <th>Срок действия</th>
                              )}
                              {activeTab === "overtime" && (
                                <th>Статус использования</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((report) => (
                              <tr key={report.id}>
                                <td>{report.date}</td>
                                {activeTab === "overtime" && (
                                  <td>{report.project}</td>
                                )}
                                <td>
                                  {activeTab === "overtime"
                                    ? report.hours
                                    : `${report.hours}ч`}
                                </td>
                                <td>
                                  <span
                                    className={`approved-reports__status ${
                                      report.status === "Согласован"
                                        ? "approved"
                                        : "rejected"
                                    }`}
                                  >
                                    {report.status}
                                  </span>
                                </td>
                                {activeTab === "overtime" && (
                                  <td>
                                    {report.validUntil && (
                                      <div>
                                        <div>До: {report.validUntil}</div>
                                        {!report.isUsed && (
                                          <div className="approved-reports__days-left">
                                            Осталось дней: {report.daysLeft}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                )}
                                {activeTab === "overtime" && (
                                  <td>
                                    {report.isUsed === true ? (
                                      <div>
                                        <div>Использован</div>
                                        {report.dateOfUse && (
                                          <div
                                            style={{
                                              fontSize: "0.9em",
                                              color: "#666",
                                            }}
                                          >
                                            {report.dateOfUse}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div>Не использован</div>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Пагинация */}
          <div className="approved-reports__pagination">
            <div className="approved-reports__pagination-info">
              <span>
                Показано{" "}
                {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} -{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} из{" "}
                {totalItems}
              </span>
              <div className="approved-reports__items-per-page">
                <label htmlFor="items-per-page">Показывать:</label>
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="approved-reports__pagination-controls">
              <button
                className="approved-reports__pagination-button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                &laquo;
              </button>

              {renderPageNumbers()}

              <button
                className="approved-reports__pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                &raquo;
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ApprovedReports;
